import { useGetMySubscription } from '@/api/subscriptions/queries'
import { userHasProSubscriberGroup } from '@/constants/subscriberGroups'
import {
  CONSULTATIONS_LIMIT,
  RECORDING_TIME_LIMIT,
  CUSTOM_PROMPTS_LIMITS,
} from '@/constants/resourceLimits'

const DEFAULT_SUBSCRIPTION_LIMITS = {
  consultationsPerMonth: CONSULTATIONS_LIMIT,
  recordingSecondsPerMonth: RECORDING_TIME_LIMIT,
  customPromptsPerType: CUSTOM_PROMPTS_LIMITS.freeText,
  outputTabsPerConsultation: CUSTOM_PROMPTS_LIMITS.tabs,
}

export const useUserStore = defineStore('user', () => {
  const userRoles = ref([])
  const userPermissions = ref([])
  const userGroups = ref([])
  const tier = ref('FREE')
  const subscriptionStatus = ref('active')
  const currentPeriodEnd = ref(null)
  const limits = ref({ ...DEFAULT_SUBSCRIPTION_LIMITS })

  const {
    data: mySubscription,
    isLoading: isMySubscriptionLoading,
    refetch: refetchMySubscription,
  } = useGetMySubscription()

  const profile = ref({
    firstName: '',
    lastName: '',
    email: '',
    attributes: {
      phoneNumber: '',
      profession: '',
      crmState: '',
      crmNumber: '',
      isProfileComplete: 'false',
    },
  })

  const parsePeriodDate = (dateValue) => {
    if (!dateValue) return null
    const periodDate = new Date(dateValue)
    if (Number.isNaN(periodDate.getTime())) return null
    return periodDate
  }

  const isProByGroup = computed(() => {
    return userHasProSubscriberGroup(userGroups.value)
  })

  const isProBySubscription = computed(() => {
    if (tier.value !== 'PRO') return false
    if (subscriptionStatus.value === 'active') return true

    const periodDate = parsePeriodDate(currentPeriodEnd.value)
    return periodDate ? periodDate.getTime() > Date.now() : false
  })

  const isProUser = computed(() => {
    return isProBySubscription.value || isProByGroup.value
  })

  const isFreeUser = computed(() => {
    return !isProUser.value && userGroups.value.includes('/SUBSCRIBERS/FREE')
  })

  const isProfileCompleted = computed(() => {
    return profile.value.attributes?.isProfileComplete === 'true'
  })

  // Campos exigidos para liberar o Evidence (independente de Free/Pro / isProfileComplete)
  const hasEvidenceRequiredProfile = computed(() => {
    const attrs = profile.value.attributes ?? {}
    const toScalar = (value) => {
      if (Array.isArray(value)) return String(value[0] ?? '').trim()
      if (value == null) return ''
      return String(value).trim()
    }
    const phoneDigits = toScalar(attrs.phoneNumber).replace(/\D/g, '')
    return Boolean(
      toScalar(profile.value.firstName) &&
        toScalar(profile.value.lastName) &&
        phoneDigits.length >= 10 &&
        toScalar(attrs.crmNumber) &&
        toScalar(attrs.crmState) &&
        toScalar(attrs.profession),
    )
  })

  const isProfessionalHealthCare = computed(() => {
    return userRoles.value.includes('DAAI_HEALTHCARE_PROFESSIONAL_ROLE')
  })

  const updateUserRoles = (roles) => {
    userRoles.value = roles
  }
  const updateUserPermissions = (permissions) => {
    userPermissions.value = permissions
  }
  const updateUserGroups = (groups) => {
    userGroups.value = groups || []
  }

  const getSanitizedLimitValue = (value, fallback) => {
    if (typeof value !== 'number') return fallback
    return Number.isFinite(value) ? value : fallback
  }

  const updateSubscriptionData = (subscription) => {
    if (!subscription) {
      tier.value = 'FREE'
      subscriptionStatus.value = 'active'
      currentPeriodEnd.value = null
      limits.value = { ...DEFAULT_SUBSCRIPTION_LIMITS }
      return
    }

    const resolvedTier = String(subscription.tier || 'FREE').toUpperCase()
    tier.value = resolvedTier
    subscriptionStatus.value = subscription.status || 'inactive'
    currentPeriodEnd.value = subscription.currentPeriodEnd || null

    const isFreeTier = resolvedTier === 'FREE'
    limits.value = {
      consultationsPerMonth: isFreeTier
        ? DEFAULT_SUBSCRIPTION_LIMITS.consultationsPerMonth
        : getSanitizedLimitValue(
            subscription.limits?.consultationsPerMonth,
            DEFAULT_SUBSCRIPTION_LIMITS.consultationsPerMonth,
          ),
      recordingSecondsPerMonth: isFreeTier
        ? DEFAULT_SUBSCRIPTION_LIMITS.recordingSecondsPerMonth
        : getSanitizedLimitValue(
            subscription.limits?.recordingSecondsPerMonth,
            DEFAULT_SUBSCRIPTION_LIMITS.recordingSecondsPerMonth,
          ),
      customPromptsPerType: isFreeTier
        ? DEFAULT_SUBSCRIPTION_LIMITS.customPromptsPerType
        : getSanitizedLimitValue(
            subscription.limits?.customPromptsPerType,
            DEFAULT_SUBSCRIPTION_LIMITS.customPromptsPerType,
          ),
      outputTabsPerConsultation: getSanitizedLimitValue(
        subscription.limits?.outputTabsPerConsultation,
        DEFAULT_SUBSCRIPTION_LIMITS.outputTabsPerConsultation,
      ),
    }
  }

  const syncMySubscription = async () => {
    const result = await refetchMySubscription()
    // On error (network/API), keep the current subscription state instead of
    // downgrading the user to FREE — a transient failure (e.g. on the checkout
    // success page) must not revoke access from someone who just paid.
    if (result.isError) return null
    updateSubscriptionData(result.data)
    return result.data
  }

  const loadUserPermissions = async () => {
    try {
      const dataParams = new URLSearchParams({
        grant_type: 'urn:ietf:params:oauth:grant-type:uma-ticket',
        audience: 'daai-permission-cli',
      })

      const token = window.accessToken ? window.accessToken : null
      if (!token) {
        throw new Error('Access token is missing')
      }

      const response = await fetch(
        `https://safe.doctorassistant.ai/auth/realms/${
          import.meta.env.VITE_KEYCLOAK_REALM
        }/protocol/openid-connect/token`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Authorization: `Bearer ${token}`,
          },
          body: dataParams,
        },
      )

      if (!response.ok) {
        throw new Error(`Network response error: ${response.statusText}`)
      }

      const data = await response.json()
      const accessToken = data.access_token
      const base64Url = accessToken.split('.')[1]
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      )

      const tokenParsed = JSON.parse(jsonPayload)
      window.tokenParsed.authorization = tokenParsed.authorization
      updateUserRoles(tokenParsed.realm_access.roles)
      updateUserPermissions(tokenParsed.authorization.permissions)
    } catch (error) {
      console.info('Error loading user permissions')
    }
  }

  const loadUserProfile = async (tokenParsed) => {
    profile.value.firstName = tokenParsed.firstName
    profile.value.lastName = tokenParsed.lastName
    profile.value.email = tokenParsed.email
    profile.value.attributes.phoneNumber = tokenParsed.phoneNumber
    profile.value.attributes.profession = tokenParsed.profession
    profile.value.attributes.crmState = tokenParsed.crmState
    profile.value.attributes.crmNumber = tokenParsed.crmNumber
    profile.value.attributes.isProfileComplete = tokenParsed.isProfileComplete
  }

  const updateLocalProfile = ({ firstName, lastName, attributes = {} }) => {
    if (firstName !== undefined) profile.value.firstName = firstName
    if (lastName !== undefined) profile.value.lastName = lastName
    profile.value.attributes = {
      ...profile.value.attributes,
      ...attributes,
    }
  }

  watch(
    mySubscription,
    (subscription) => {
      updateSubscriptionData(subscription)
    },
    { immediate: true },
  )

  return {
    userRoles,
    userPermissions,
    userGroups,
    isFreeUser,
    isProfessionalHealthCare,
    updateUserRoles,
    updateUserPermissions,
    updateUserGroups,
    loadUserPermissions,
    loadUserProfile,
    updateLocalProfile,
    isProfileCompleted,
    hasEvidenceRequiredProfile,
    profile,
    isProUser,
    isProBySubscription,
    tier,
    subscriptionStatus,
    currentPeriodEnd,
    limits,
    isMySubscriptionLoading,
    syncMySubscription,
  }
})
